import { useEffect, useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import { useDaftarTenant, usePemakaian } from '../../../domains/tenancy/api'
import { angka, tanggal, uangUSD } from '../../../shared/lib/waktu'
import {
  Angka, Baris, Bendera, Gagal, KepalaHalaman, Kosong, Memuat, Panel, Pilihan, Tabel,
} from '../../../shared/ui'

const RENTANG = [7, 30, 90]

export default function Pemakaian() {
  const { t, locale } = useBahasa()
  const [tenant, setTenant] = useState('')
  const [hari, setHari] = useState(30)

  const daftar = useDaftarTenant()
  const q = usePemakaian(tenant, hari)

  // Tenant WAJIB dipilih — `get_tenant_id` menolak melayani operator tanpa
  // `X-Tenant-Id`, jadi halaman tanpa pilihan akan selalu 400. Yang pertama dipilih
  // otomatis supaya halaman tidak dibuka dalam keadaan yang mustahil berhasil.
  useEffect(() => {
    if (!tenant && daftar.data?.length) setTenant(daftar.data[0].id)
  }, [daftar.data, tenant])

  const d = q.data

  return (
    <>
      <KepalaHalaman
        judul={t('pk.judul')}
        keterangan={t('pk.keterangan')}
        aksi={
          <span className="flex items-center gap-2">
            <Pilihan value={tenant} onChange={(e) => setTenant(e.target.value)}>
              {(daftar.data ?? []).map((x) => (
                <option key={x.id} value={x.id}>{x.name}</option>
              ))}
            </Pilihan>
            <Pilihan value={hari} onChange={(e) => setHari(Number(e.target.value))}>
              {RENTANG.map((n) => (
                <option key={n} value={n}>{t('pk.hari', { n })}</option>
              ))}
            </Pilihan>
          </span>
        }
      />

      {q.isLoading && <Memuat tinggi={4} />}
      {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

      {d && (
        <>
          <div className="flex flex-wrap gap-2">
            <Angka label={t('pk.totalPanggilan')} nilai={d.total_count} locale={locale} />
            <Angka label={t('pk.totalToken')} nilai={d.total_tokens} locale={locale} />
            {/* Biaya ditampilkan sebagai "$1.23" hanya kalau SELURUH panggilan punya
                tarif. Kalau ada yang belum, ia "≥ $1.23" — batas bawah, bukan total,
                dan bedanya dinyatakan dengan kata, bukan dengan tanda yang harus
                ditebak sendiri. */}
            <Angka
              label={t('pk.totalBiaya')}
              nilai={(d.total_tanpa_tarif > 0 ? '≥ ' : '') + uangUSD(d.total_cost_usd, locale)}
              locale={locale}
            />
          </div>

          {/*
            Peringatan ini muncul HANYA kalau ada panggilan yang tarifnya belum diisi.
            Kolom biaya sendiri sudah nyata sejak 9 September: `ai_models.tarif` diisi
            dari OpenRouter, dan `panggilan_tanpa_tarif` menghitung yang belum terjawab.

            Selama masih ada yang belum, totalnya BATAS BAWAH — dan itu harus dikatakan.
            Angka yang terdengar lengkap padahal kurang persis sebab kolom ini dulu
            sengaja disembunyikan.
          */}
          {d.total_tanpa_tarif > 0 && (
            <Bendera nada="warn" className="max-w-[76ch]">{t('pk.biayaBelumLengkap', { n: angka(d.total_tanpa_tarif, locale) })}</Bendera>
          )}

          {/* KE MANA PERGINYA — pemakaian per KEGIATAN, bukan per model.
              Orang yang membuka layar biaya menanyakan "uang saya habis untuk apa",
              dan model tidak menjawabnya: satu model yang sama menulis konsep DAN
              membalas chat, dengan biaya per panggilan yang jauh berbeda. */}
          <Panel judul={t('pk.keMana')} catatan={t('pk.keManaCatatan')}>
            {(d.per_kegiatan ?? []).length === 0 ? (
              <Kosong judul={t('pk.kosong')} />
            ) : (
              <Tabel kepala={[t('pk.kolKegiatan'), t('pk.kolJumlah'), t('pk.kolToken'),
                              t('pk.kolBiaya')]}>
                {d.per_kegiatan.map((k) => (
                  <Baris key={k.kegiatan}>
                    <td>
                      <span className="font-semibold">{k.label}</span>
                      <span className="block font-mono text-[10.5px] text-ink3">{k.kegiatan}</span>
                    </td>
                    <td className="tabular">{k.count}</td>
                    <td className="tabular">{k.tokens_used || '—'}</td>
                    <td className="tabular">
                      {uangUSD(k.cost_estimate_usd, locale)}
                      {k.panggilan_tanpa_tarif > 0 && (
                        <span className="block text-[10.5px] text-warn">
                          {t('pk.tanpaTarif', { n: k.panggilan_tanpa_tarif })}
                        </span>
                      )}
                    </td>
                  </Baris>
                ))}
              </Tabel>
            )}
          </Panel>

          {/* JUDULNYA DIPERSEMPIT KE APA YANG BENAR-BENAR DIHITUNGNYA.
              Dulu "Per model" — dan datanya cuma `engagement_queue`, yaitu balasan chat.
              Generasi gambar, video, dan drafting tidak masuk sama sekali. Pada data dev
              11 September itu berarti panelnya menampilkan 4 balasan DeepSeek sambil
              menyembunyikan 12 pembuatan gambar: kategori TERBESAR, hilang di balik judul
              yang menjanjikan semuanya.
              `daily_usage` tidak menyimpan `model_used`, jadi rincian per-model lintas
              kegiatan memang belum punya datanya — dicatat di BUGS_PENDING. */}
          <Panel judul={t('pk.perModel')} catatan={t('pk.perModelCatatan')}>
            {d.models.length === 0 ? (
              <Kosong judul={t('pk.belumAdaModel')} />
            ) : (
              <Tabel kepala={[t('pk.kolModel'), t('pk.kolBalasan')]}>
                {d.models.map((m) => (
                  <Baris key={m.model_used ?? '(tanpa model)'}>
                    {/* `model_used` NULLABLE — baris tanpa model dirender sebagai sel
                        kosong bernilai angka, yang terbaca seperti kerusakan tabel.
                        Terjadi di dev: satu baris `None` dengan hitungan 1. */}
                    <td className="font-mono text-[11.5px]">
                      {m.model_used || <span className="text-ink3">{t('pk.modelTakTercatat')}</span>}
                    </td>
                    <td className="tabular">{angka(m.balasan, locale)}</td>
                  </Baris>
                ))}
              </Tabel>
            )}
          </Panel>

          <Panel judul={t('pk.perHari')}>
            {d.days.length === 0 ? (
              <Kosong
                judul={t('pk.kosong')}
                keterangan={t('pk.kosongKeterangan', { n: hari })}
              />
            ) : (
              <Tabel
                kepala={[
                  t('pk.kolTanggal'), t('pk.kolJenis'), t('pk.kolPelaku'),
                  t('pk.kolJumlah'), t('pk.kolToken'), t('pk.kolBiaya'),
                ]}
              >
                {d.days.map((r, i) => (
                  <Baris key={`${r.date}-${r.label || r.resource_type}-${r.telegram_user_id}-${i}`}>
                    <td className="font-mono text-[11.5px] whitespace-nowrap">
                      {tanggal(r.date, locale)}
                    </td>
                    <td className="font-mono text-[11.5px] text-ink2">{r.label || r.resource_type}</td>
                    <td>{r.display_name}</td>
                    <td className="tabular">{angka(r.count, locale)}</td>
                    <td className="tabular">{angka(r.tokens_used, locale)}</td>
                    <td className="tabular">
                      {r.panggilan_tanpa_tarif > 0 ? '≥ ' : ''}{uangUSD(r.cost_estimate_usd, locale)}
                    </td>
                  </Baris>
                ))}
              </Tabel>
            )}
          </Panel>
        </>
      )}
    </>
  )
}
